import React, { useCallback, useEffect, useId, useRef, useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import ThemeToggleButton from './ThemeToggleButton';
import { useModalSelectionGuard, preventModalOverlaySelection } from '../hooks/useModalSelectionGuard';
import { MOBILE_LAYOUT_MAX_WIDTH } from '../constants/layout';
import './AppNav.css';

const NAV_ICONS = {
  plan: '/icons/nav/plan.png',
  journal: '/icons/nav/journal.png',
  administration: '/icons/nav/administration.png',
};

function NavIcon({ name, inFabCell = false }) {
  const src = NAV_ICONS[name];
  const icon = (
    <span
      className="app-nav-link-icon"
      style={{
        WebkitMaskImage: `url(${src})`,
        maskImage: `url(${src})`,
      }}
      aria-hidden="true"
    />
  );

  if (inFabCell) {
    return <span className="app-nav-fab-icon-cell">{icon}</span>;
  }

  return icon;
}

function getInitial(email) {
  if (!email) return '?';
  return email.trim().charAt(0).toUpperCase();
}

function MenuIcon() {
  return (
    <svg width="22" height="22" viewBox="0 0 22 22" fill="none" aria-hidden="true">
      <path
        d="M4 6.5h14M4 11h14M4 15.5h14"
        stroke="currentColor"
        strokeWidth="1.75"
        strokeLinecap="round"
      />
    </svg>
  );
}

function CloseIcon() {
  return (
    <svg width="22" height="22" viewBox="0 0 22 22" fill="none" aria-hidden="true">
      <path
        d="M6 6l10 10M16 6L6 16"
        stroke="currentColor"
        strokeWidth="1.75"
        strokeLinecap="round"
      />
    </svg>
  );
}

function AccountRobotNavIcon({ inFabCell = false }) {
  const icon = (
    <span className="app-nav-link-icon app-nav-link-icon--svg" aria-hidden="true">
      <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
        <rect x="1.5" y="3" width="13" height="10" rx="1.5" stroke="currentColor" strokeWidth="1.25" />
        <path d="M4.5 6.5h7M4.5 9.5h4.5" stroke="currentColor" strokeWidth="1.25" strokeLinecap="round" />
        <circle cx="11.5" cy="5" r="2.25" stroke="currentColor" strokeWidth="1.1" />
        <path d="M11.5 4.2v1.6M10.9 5h1.2" stroke="currentColor" strokeWidth="0.9" strokeLinecap="round" />
      </svg>
    </span>
  );

  if (inFabCell) {
    return <span className="app-nav-fab-icon-cell">{icon}</span>;
  }

  return icon;
}

function AppNavTabs({ isPromo, isAdmin, linkClassName, onNavigate, inFabCell = false, includeAccountRobot = false }) {
  if (isPromo) return null;

  return (
    <nav className="app-nav-tabs" aria-label="Основная навигация">
      <NavLink to="/" end className={linkClassName} onClick={onNavigate}>
        <NavIcon name="plan" inFabCell={inFabCell} />
        <span className="app-nav-fab-link-label">План</span>
      </NavLink>
      <NavLink to="/deals" className={linkClassName} onClick={onNavigate}>
        <NavIcon name="journal" inFabCell={inFabCell} />
        <span className="app-nav-fab-link-label">Журнал</span>
      </NavLink>
      {includeAccountRobot && (
        <NavLink to="/account-robot" className={linkClassName} onClick={onNavigate}>
          <AccountRobotNavIcon inFabCell={inFabCell} />
          <span className="app-nav-fab-link-label">Счет и робот</span>
        </NavLink>
      )}
      {isAdmin && (
        <NavLink to="/administration" className={linkClassName} onClick={onNavigate}>
          <NavIcon name="administration" inFabCell={inFabCell} />
          <span className="app-nav-link-label app-nav-link-label--full app-nav-fab-link-label">Администрирование</span>
          <span className="app-nav-link-label app-nav-link-label--short app-nav-fab-link-label">Админ</span>
        </NavLink>
      )}
    </nav>
  );
}

function AppNavAccount({ user, isPromo, onOpenSecrets, variant }) {
  if (isPromo) {
    return (
      <div className={`app-nav-account app-nav-account--static${variant === 'mobile' ? ' app-nav-fab-account-row app-nav-fab-account-row--static' : ''}`}>
        <span className="app-nav-avatar" aria-hidden="true">{getInitial(user.email)}</span>
        <span className="app-nav-account-text">
          <span className="app-nav-account-label">Аккаунт</span>
          <span className="app-nav-email" title={user.email}>{user.email}</span>
        </span>
        {variant === 'desktop' && <ThemeToggleButton />}
      </div>
    );
  }

  if (variant === 'mobile') {
    return (
      <button
        type="button"
        className="app-nav-fab-account-row app-nav-fab-account-row--button"
        onClick={onOpenSecrets}
        title="Настройки брокерских ключей"
      >
        <span className="app-nav-avatar" aria-hidden="true">{getInitial(user.email)}</span>
        <span className="app-nav-account-text">
          <span className="app-nav-account-label">Аккаунт</span>
          <span className="app-nav-email">{user.email}</span>
        </span>
      </button>
    );
  }

  return (
    <div className="app-nav-account">
      <button
        type="button"
        className="app-nav-account-main"
        onClick={onOpenSecrets}
        title="Настройки брокерских ключей"
      >
        <span className="app-nav-avatar" aria-hidden="true">{getInitial(user.email)}</span>
        <span className="app-nav-account-text">
          <span className="app-nav-account-label">Аккаунт</span>
          <span className="app-nav-email">{user.email}</span>
        </span>
      </button>
      <ThemeToggleButton />
    </div>
  );
}

function ChevronRightIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden="true">
      <path
        d="M4.25 2.5L7.75 6l-3.5 3.5"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function AppNav({ user, isAdmin, isPromo, onLogout, onOpenSecrets }) {
  const [isOpen, setIsOpen] = useState(false);
  const [isFabHidden, setIsFabHidden] = useState(() => {
    try {
      return sessionStorage.getItem('appNav.fabHidden') === '1';
    } catch {
      return false;
    }
  });
  const fabSwipeRef = useRef(null);
  const suppressFabClickUntilRef = useRef(0);
  const menuId = useId();
  const location = useLocation();

  useModalSelectionGuard(isOpen);

  const closeMenu = useCallback(() => setIsOpen(false), []);

  const hideFab = useCallback(() => {
    closeMenu();
    setIsFabHidden(true);
    try {
      sessionStorage.setItem('appNav.fabHidden', '1');
    } catch {
      /* ignore */
    }
  }, [closeMenu]);

  const showFab = useCallback(() => {
    suppressFabClickUntilRef.current = 0;
    setIsFabHidden(false);
    try {
      sessionStorage.removeItem('appNav.fabHidden');
    } catch {
      /* ignore */
    }
  }, []);

  const desktopLinkClassName = ({ isActive }) =>
    `app-nav-link${isActive ? ' app-nav-link--active' : ''}`;

  const mobileLinkClassName = ({ isActive }) =>
    `app-nav-menu-link${isActive ? ' app-nav-menu-link--active' : ''}`;

  useEffect(() => {
    closeMenu();
  }, [location.pathname, closeMenu]);

  useEffect(() => {
    if (!isOpen) return undefined;

    const onKeyDown = (event) => {
      if (event.key === 'Escape') {
        closeMenu();
      }
    };

    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [isOpen, closeMenu]);

  useEffect(() => {
    const mediaQuery = window.matchMedia(`(max-width: ${MOBILE_LAYOUT_MAX_WIDTH}px)`);

    const handleChange = (event) => {
      if (!event.matches) {
        closeMenu();
        setIsFabHidden(false);
      }
    };

    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, [closeMenu]);

  const handleOpenSecrets = () => {
    closeMenu();
    onOpenSecrets();
  };

  const handleLogout = () => {
    closeMenu();
    onLogout();
  };

  const handleFabTouchStart = (event) => {
    if (isOpen || isFabHidden) {
      return;
    }
    const touch = event.touches[0];
    fabSwipeRef.current = { x: touch.clientX, y: touch.clientY };
  };

  const handleFabTouchEnd = (event) => {
    if (!fabSwipeRef.current || isOpen || isFabHidden) {
      return;
    }
    const touch = event.changedTouches[0];
    const dx = touch.clientX - fabSwipeRef.current.x;
    const dy = touch.clientY - fabSwipeRef.current.y;
    fabSwipeRef.current = null;
    if (dx < -48 && Math.abs(dx) > Math.abs(dy)) {
      suppressFabClickUntilRef.current = Date.now() + 400;
      hideFab();
    }
  };

  const handleFabTriggerClick = () => {
    if (Date.now() < suppressFabClickUntilRef.current) {
      return;
    }
    setIsOpen((open) => !open);
  };

  return (
    <>
      <header className={`app-nav app-nav--desktop${isPromo ? ' app-nav--promo' : ''}`}>
        <div className="app-nav-inner">
          <div className="app-nav-start">
            <NavLink to="/" end className="app-nav-brand" title="На главную — План">
              <span className="app-nav-brand-mark" aria-hidden="true" />
              <span className="app-nav-brand-text">Exponenta</span>
            </NavLink>

            <AppNavTabs
              isPromo={isPromo}
              isAdmin={isAdmin}
              linkClassName={desktopLinkClassName}
            />
          </div>

          <div className="app-nav-user">
            <AppNavAccount
              user={user}
              isPromo={isPromo}
              onOpenSecrets={onOpenSecrets}
              variant="desktop"
            />
            <button type="button" className="app-nav-logout" onClick={onLogout}>
              Выйти
            </button>
          </div>
        </div>
      </header>

      <div className="app-nav-mobile">
        {isOpen && (
          <button
            type="button"
            className="app-nav-fab-backdrop"
            aria-label="Закрыть меню"
            onClick={closeMenu}
            onSelectStart={preventModalOverlaySelection}
          />
        )}

        {isFabHidden && (
          <button
            type="button"
            className="app-nav-fab-restore-tab"
            aria-label="Показать меню"
            onClick={showFab}
          >
            <ChevronRightIcon />
          </button>
        )}

        <div
          className={`app-nav-fab-dock${isFabHidden ? ' app-nav-fab-dock--hidden' : ''}`}
          onTouchStart={handleFabTouchStart}
          onTouchEnd={handleFabTouchEnd}
        >
          <div className={`app-nav-fab${isOpen ? ' app-nav-fab--open' : ''}`}>
            <button
              type="button"
              className="app-nav-fab-trigger"
              aria-expanded={isOpen}
              aria-controls={menuId}
              aria-label={isOpen ? 'Закрыть меню' : 'Открыть меню'}
              onClick={handleFabTriggerClick}
            >
              {isOpen ? <CloseIcon /> : <MenuIcon />}
            </button>

            <div
              id={menuId}
              className="app-nav-fab-panel"
              role="dialog"
              aria-modal="true"
              aria-hidden={!isOpen}
              aria-label="Меню приложения"
              onSelectStart={isOpen ? preventModalOverlaySelection : undefined}
            >
              <div className="app-nav-fab-panel-inner">
                <div className="app-nav-fab-brand">
                  <span className="app-nav-brand-mark" aria-hidden="true" />
                  <span className="app-nav-fab-brand-text">Exponenta</span>
                </div>

                <AppNavTabs
                  isPromo={isPromo}
                  isAdmin={isAdmin}
                  linkClassName={mobileLinkClassName}
                  onNavigate={closeMenu}
                  inFabCell
                  includeAccountRobot
                />

                <div className="app-nav-fab-section-divider" aria-hidden="true" />

                <div className="app-nav-fab-account">
                  <ThemeToggleButton variant="fab-row" onAfterToggle={closeMenu} />
                  <AppNavAccount
                    user={user}
                    isPromo={isPromo}
                    onOpenSecrets={handleOpenSecrets}
                    variant="mobile"
                  />
                </div>

                <div className="app-nav-fab-section-divider" aria-hidden="true" />

                <div className="app-nav-fab-logout-row">
                  <button type="button" className="app-nav-fab-logout" onClick={handleLogout}>
                    Выйти
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default AppNav;
